import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt,
  FaServer, 
  FaCheckCircle,
  FaArrowRight,
  FaPhone,
  FaExternalLinkAlt,
  FaMicrochip,
  FaMemory,
  FaHdd,
  FaRocket,
  FaCog,
  FaNetworkWired,
  FaDatabase,
  FaCloud,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaChevronLeft,
  FaChevronRight,
  FaLock,
  FaTachometerAlt,
  FaExpandArrowsAlt,
  FaCube
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Import du hook de langue
import { Helmet } from 'react-helmet-async';

const Backup = () => {
  const { t, lang } = useLanguage();
  const backupT = t.backup; // Raccourci pour les traductions backup

  // Animation hooks
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSolutionsRef, solutionsInView] = useScrollAnimation(0.1);

  // Reconstruction dynamique des données avec les traductions
  // On garde les icônes et les images ici, mais on prend les textes du contexte
  const BACKUP_DATA = useMemo(() => ({
    categories: [
      {
        id: 'physical-appliance',
        name: backupT.categories['physical-appliance'].name,
        description: backupT.categories['physical-appliance'].desc,
        icon: <FaServer />
      },
      {
        id: 'virtual-edition',
        name: backupT.categories['virtual-edition'].name,
        description: backupT.categories['virtual-edition'].desc,
        icon: <FaCloud />
      },
      {
        id: 'all-flash',
        name: backupT.categories['all-flash'].name,
        description: backupT.categories['all-flash'].desc,
        icon: <FaTachometerAlt />
      },
      {
        id: 'cyber-recovery',
        name: backupT.categories['cyber-recovery'].name,
        description: backupT.categories['cyber-recovery'].desc,
        icon: <FaShieldAlt />
      }
    ],
    solutions: [
      {
        id: 'dd9910',
        ...backupT.products.dd9910,
        category: 'physical-appliance',
        isNew: false,
        image: '/images/Backup/dd9910.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        // On s'assure que les specs matchent les clés utilisées dans le rendu
        specs: {
          processor: backupT.products.dd9910.specs.cpu,
          memory: backupT.products.dd9910.specs.cap,
          storage: backupT.products.dd9910.specs.st,
          network: backupT.products.dd9910.specs.net
        }
      },
      {
        id: 'dd9410',
        ...backupT.products.dd9410,
        category: 'physical-appliance',
        isNew: false,
        image: '/images/Backup/dd9410.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        specs: {
          processor: backupT.products.dd9410.specs.cpu,
          memory: backupT.products.dd9410.specs.cap,
          storage: backupT.products.dd9410.specs.st,
          network: backupT.products.dd9410.specs.net
        }
      },
      {
        id: 'dd6410',
        ...backupT.products.dd6410,
        category: 'physical-appliance',
        isNew: false,
        image: '/images/Backup/dd6410.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        specs: {
          processor: backupT.products.dd6410.specs.cpu,
          memory: backupT.products.dd6410.specs.cap,
          storage: backupT.products.dd6410.specs.st,
          network: backupT.products.dd6410.specs.net
        }
      },
      {
        id: 'all-flash-ready',
        ...backupT.products.flash,
        category: 'all-flash',
        isNew: true,
        image: '/images/Backup/all-flash-ready.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        specs: {
          processor: backupT.products.flash.specs.cpu,
          memory: backupT.products.flash.specs.cap,
          storage: backupT.products.flash.specs.st,
          network: backupT.products.flash.specs.net
        }
      },
      {
        id: 'ddve',
        ...backupT.products.ddve,
        category: 'virtual-edition',
        isNew: false,
        image: '/images/Backup/ddve.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        specs: {
          processor: backupT.products.ddve.specs.cpu,
          memory: backupT.products.ddve.specs.cap,
          storage: backupT.products.ddve.specs.st,
          network: backupT.products.ddve.specs.net
        }
      },
      {
        id: 'cyber-recovery',
        ...backupT.products.cyber,
        category: 'cyber-recovery',
        isNew: false,
        image: '/images/Backup/cyber-recovery.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        specs: {
          processor: backupT.products.cyber.specs.cpu,
          memory: backupT.products.cyber.specs.cap,
          storage: backupT.products.cyber.specs.st,
          network: backupT.products.cyber.specs.net
        }
      }
    ]
  }), [backupT]);

  // État des filtres et recherche
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  // Animation variants
  const animationVariants = {
    fadeInUp: {
      initial: { opacity: 0, y: 30 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.8 }
    },
    scaleIn: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.6 }
    }
  };

  // Filtrage des solutions
  const filteredSolutions = useMemo(() => {
    return BACKUP_DATA.solutions.filter(solution => {
      const matchCategory = selectedCategory === 'all' || solution.category === selectedCategory;
      const matchSearch = solution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         solution.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         solution.workloads.some(workload => workload.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchTerm, BACKUP_DATA]);

  // Pagination
  const paginatedSolutions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSolutions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSolutions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSolutions.length / itemsPerPage);

  // Handlers
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const scrollToSolutions = () => {
    document.getElementById('solutions-section').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div>

      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.backup.seo.title}</title>
        <meta name="description" content={t.backup.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/backup" />
        
        {/* Balises pour LinkedIn / Facebook */}
        <meta property="og:title" content={t.backup.seo.title} />
        <meta property="og:description" content={t.backup.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/Backup/dd9410.png" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* ---------------------------------- */}


      {/* Section Hero */}
      <section 
        ref={setHeroRef}
        className="hero-section-sii" 
        style={{ 
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }}
        />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  {...animationVariants.scaleIn}
                  transition={{ delay: 0.2 }}
                  className="mb-4"
                >
                  <span className="badge-sii">
                    <FaShieldAlt className="me-2" />
                    {backupT.hero.badge}
                  </span>
                </motion.div>

                <motion.h1 
                  className="display-3 fw-bold mb-4 text-white"
                  initial={{ opacity: 0, x: -50 }}
                  animate={heroInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{
                    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                    lineHeight: '1.1',
                    fontWeight: '700'
                  }}
                  dangerouslySetInnerHTML={{ __html: backupT.hero.title }}
                />

                <motion.p 
                  className="lead text-light mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                    maxWidth: '600px',
                    lineHeight: '1.6',
                    color: 'rgba(255,255,255,0.9)'
                  }}
                >
                  {backupT.hero.sub}
                </motion.p>

                <motion.div
                  className="d-flex flex-wrap gap-3 mb-5"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <Button 
                    size="lg" 
                    onClick={scrollToSolutions}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: '#d90000',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaRocket className="me-2" />
                    {backupT.hero.btn1}
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="lg"
                    onClick={() => {
                        window.location.href = '/#contact';
                        setTimeout(() => {
                          document.getElementById('contact')?.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }, 100);
                      }}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px',
                      background: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaPhone className="me-2" />
                    {backupT.hero.btn2}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center"
              >
                <div 
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '24px',
                    padding: '3rem',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h3 className="text-white mb-4 fw-bold">
                    {backupT.hero.whyTitle}
                  </h3>
                  <div className="text-start">
                    {backupT.hero.whyList.map((feature, index) => (
                      <motion.div
                        key={index}
                        className="d-flex align-items-center mb-3"
                        initial={{ opacity: 0, x: -20 }}
                        animate={heroInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                      >
                        <FaCheckCircle 
                          className="me-3 text-success" 
                          style={{ minWidth: '20px' }}
                        />
                        <span className="text-white">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section Catégories */}
      <section className="categories-section py-5 bg-light">
        <Container fluid>
          <motion.div
            ref={setCategoriesRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={categoriesInView ? "animate" : "initial"}
            className="text-center mb-5"
          >
            <h2 className="fw-bold mb-3" dangerouslySetInnerHTML={{ __html: backupT.categories.title }} />
            <p className="text-muted lead">
              {backupT.categories.subtitle}
            </p>
          </motion.div>

          <Row className="g-4 justify-content-center">
            {BACKUP_DATA.categories.map((category, index) => (
              <Col key={category.id} lg={3} md={6}>
                <motion.div
                  variants={animationVariants.scaleIn}
                  initial="initial"
                  animate={categoriesInView ? "animate" : "initial"}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className="h-100 text-center border-0 shadow-sm category-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    <Card.Body className="p-4">
                      <div className="mb-3" style={{ fontSize: '3rem', color: '#d90000' }}>
                        {category.icon}
                      </div>
                      <h5 className="fw-bold mb-2">{category.name}</h5>
                      <p className="text-muted small">{category.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Section Solutions */}
      <section id="solutions-section" className="solutions-section py-5">
        <Container fluid>
          <motion.div
            ref={setSolutionsRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={solutionsInView ? "animate" : "initial"}
            className="mb-5"
          >
            <Row>
              {/* Sidebar Filtres */}
              <Col lg={3}>
                <div style={{
                  position: 'sticky',
                  top: '100px',
                  background: '#fff',
                  borderRadius: '20px',
                  padding: '2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef',
                  height: 'fit-content'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '2rem',
                    paddingBottom: '1rem',
                    borderBottom: '2px solid #e9ecef'
                  }}>
                    <FaFilter style={{ 
                      fontSize: '1.2rem', 
                      color: '#d90000',
                      marginRight: '0.75rem'
                    }} />
                    <h5 style={{
                      margin: 0,
                      fontWeight: 700,
                      color: '#1a202c',
                      fontSize: '1.1rem'
                    }}>
                      {backupT.sidebar.title}
                    </h5>
                  </div>

                  <div className="mb-4">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {backupT.sidebar.search}
                    </label>
                    <div className="position-relative">
                      <FaSearch 
                        style={{
                          position: 'absolute',
                          left: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          fontSize: '0.9rem'
                        }}
                      />
                      <Form.Control
                        type="text"
                        placeholder={backupT.sidebar.searchPl}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e0e0e0',
                          paddingLeft: '2.5rem',
                          paddingRight: '1rem',
                          paddingTop: '0.75rem',
                          paddingBottom: '0.75rem',
                          fontSize: '0.9rem',
                          background: '#f8f9fa'
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {backupT.sidebar.cat}
                    </label>
                    
                    <div className="d-grid gap-2">
                      <Button
                        variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'}
                        onClick={() => handleCategoryChange('all')}
                        style={{
                          borderRadius: '12px',
                          padding: '0.75rem 1rem',
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          border: selectedCategory === 'all' ? '2px solid #d90000' : '1px solid #dee2e6'
                        }}
                      >
                        <FaShieldAlt className="me-2" style={{ fontSize: '0.8rem' }} />
                        {backupT.sidebar.all} ({BACKUP_DATA.solutions.length})
                      </Button>

                      {BACKUP_DATA.categories.map(category => (
                        <Button
                          key={category.id}
                          variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'}
                          onClick={() => handleCategoryChange(category.id)}
                          style={{
                            borderRadius: '12px',
                            padding: '0.75rem 1rem',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            textAlign: 'left',
                            border: selectedCategory === category.id ? '2px solid #d90000' : '1px solid #dee2e6'
                          }}
                        >
                          <span className="me-2" style={{ fontSize: '0.8rem' }}>
                            {category.icon}
                          </span>
                          {category.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    borderRadius: '12px',
                    border: '1px solid #dee2e6',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ 
                      color: '#495057', 
                      margin: 0,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      textAlign: 'center'
                    }}>
                      <strong style={{ color: '#d90000' }}>{filteredSolutions.length}</strong> {backupT.sidebar.results}
                    </p>
                    {totalPages > 1 && (
                      <p style={{ 
                        color: '#6c757d', 
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.75rem',
                        textAlign: 'center'
                      }}>
                        Page {currentPage} {backupT.sidebar.pageOf} {totalPages}
                      </p>
                    )}
                  </div>

                  <div className="mb-3">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {backupT.sidebar.view}
                    </label>
                    
                    <div className="btn-group d-grid" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                      <Button
                        variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'}
                        onClick={() => setViewMode('grid')}
                        style={{ border: 'none', fontSize: '0.9rem', borderRadius: '12px 12px 0 0' }}
                      >
                        <FaTh className="me-2" /> {backupT.sidebar.grid}
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'danger' : 'outline-secondary'}
                        onClick={() => setViewMode('list')}
                        style={{ border: 'none', fontSize: '0.9rem', borderRadius: '0 0 12px 12px' }}
                      >
                        <FaList className="me-2" /> {backupT.sidebar.list}
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Contenu principal */}
              <Col lg={9}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h3 className="fw-bold mb-1" dangerouslySetInnerHTML={{ __html: backupT.titleSection }} />
                    <p className="text-muted mb-0">
                      {filteredSolutions.length} {backupT.available}
                    </p>
                  </div>
                </div>

                {viewMode === 'grid' ? (
                  <Row className="g-4 mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <Col key={solution.id} lg={6} md={6}>
                        <motion.div
                          variants={animationVariants.scaleIn}
                          initial="initial"
                          animate={solutionsInView ? "animate" : "initial"}
                          transition={{ delay: index * 0.1 }}
                          style={{ height: '100%' }}
                        >
                          <Card className="h-100 shadow-sm border-0 solution-card">
                            <div 
                              className="position-relative overflow-hidden"
                              style={{ 
                                height: '220px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                              }}
                            >
                              <div className="position-absolute top-0 end-0 p-3 d-flex flex-column gap-2" style={{ zIndex: 2 }}>
                                {solution.isNew && (
                                  <Badge bg="success" className="px-2 py-1">
                                    <small>{backupT.card.new}</small>
                                  </Badge>
                                )}
                                {solution.badge && (
                                  <Badge bg="danger" className="px-2 py-1">
                                    <small>{solution.badge}</small>
                                  </Badge>
                                )}
                              </div>

                              {solution.image && (
                                <img 
                                  src={solution.image} 
                                  alt={solution.name}
                                  style={{
                                    width: solution.imageSize?.width || '100%',
                                    height: solution.imageSize?.height || '100%',
                                    objectFit: solution.imageSize?.objectFit || 'cover',
                                    objectPosition: solution.imagePosition?.objectPosition || 'center center',
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                />
                              )}
                            </div>

                            <Card.Body className="d-flex flex-column">
                              <div className="mb-3">
                                <h5 className="fw-bold mb-2">{solution.name}</h5>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  {solution.workloads.slice(0, 3).map((workload, idx) => (
                                    <Badge key={idx} bg="light" text="dark" className="small">
                                      {workload}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              <div className="specs-grid mb-3">
                                <div className="row g-2">
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaMicrochip className="text-danger" />
                                      <div>
                                        <small className="text-muted">{backupT.card.cpu}</small>
                                        <div className="small fw-semibold">{solution.specs.processor}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaMemory className="text-danger" />
                                      <div>
                                        <small className="text-muted">{backupT.card.cap}</small>
                                        <div className="small fw-semibold">{solution.specs.memory}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaHdd className="text-danger" />
                                      <div>
                                        <small className="text-muted">{backupT.card.st}</small>
                                        <div className="small fw-semibold">{solution.specs.storage}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaNetworkWired className="text-danger" />
                                      <div>
                                        <small className="text-muted">{backupT.card.net}</small>
                                        <div className="small fw-semibold">{solution.specs.network}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mb-3 flex-grow-1">
                                <small className="text-muted text-uppercase fw-semibold">{backupT.card.highlights}</small>
                                <ul className="list-unstyled mt-2 mb-0">
                                  {solution.features.slice(0, 4).map((feature, idx) => (
                                    <li key={idx} className="d-flex align-items-start gap-2 mb-1">
                                      <FaCheckCircle className="text-success mt-1" size={12} />
                                      <small>{feature}</small>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  /* Vue Liste */
                  <div className="mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <motion.div
                        key={solution.id}
                        variants={animationVariants.fadeInUp}
                        initial="initial"
                        animate={solutionsInView ? "animate" : "initial"}
                        transition={{ delay: index * 0.1 }}
                        className="mb-3"
                      >
                        <Card className="border-0 shadow-sm">
                          <Card.Body>
                            <Row className="align-items-center">
                              <Col lg={2}>
                                <div style={{
                                  position: 'relative', borderRadius: '12px', overflow: 'hidden',
                                  backgroundColor: '#f8f9fa', padding: '0.5rem', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', width: '140px', height: '140px'
                                }}>
                                  {solution.image && (
                                    <img 
                                      src={solution.image} 
                                      alt={solution.name}
                                      style={{
                                        width: solution.imageSize?.width || '100%',
                                        height: solution.imageSize?.height || '100%',
                                        objectFit: solution.imageSize?.objectFit || 'cover',
                                        borderRadius: '12px'
                                      }}
                                    />
                                  )}
                                </div>
                              </Col>

                              <Col lg={10}>
                                <Row>
                                  <Col lg={8}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <h5 className="fw-bold mb-0">{solution.name}</h5>
                                      {solution.badge && (
                                        <Badge bg="danger" className="px-2 py-1">
                                          <small>{solution.badge}</small>
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                      {solution.workloads.map((workload, idx) => (
                                        <Badge key={idx} bg="light" text="dark" className="small">
                                          {workload}
                                        </Badge>
                                      ))}
                                    </div>

                                    <div className="mb-2">
                                      <small className="text-muted text-uppercase fw-semibold">{backupT.card.highlights}</small>
                                      <ul className="list-unstyled mt-1 mb-0">
                                        {solution.features.slice(0, 3).map((feature, idx) => (
                                          <li key={idx} className="d-flex align-items-start gap-2 mb-1">
                                            <FaCheckCircle className="text-success mt-1" size={12} />
                                            <small>{feature}</small>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </Col>

                                  <Col lg={4}>
                                    <div className="specs-summary">
                                      <small className="text-muted text-uppercase fw-semibold mb-2 d-block">
                                        Spécifications
                                      </small>
                                      <div className="d-flex flex-column gap-1">
                                        <div className="d-flex align-items-center gap-2">
                                          <FaMicrochip className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.processor}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <FaMemory className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.memory}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <FaHdd className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.storage}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <FaNetworkWired className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.network}</small>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <Button
                      variant="outline-danger" size="sm" disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <FaChevronLeft />
                    </Button>
                    <div className="d-flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page} variant={currentPage === page ? "danger" : "outline-secondary"}
                          size="sm" onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline-danger" size="sm" disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <FaChevronRight />
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section 
        className="cta-section-sii" 
        style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
      >
        <Container>
          <Row>
            <Col lg={12} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                  borderRadius: '24px', padding: '4rem 3rem', color: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', gap: '1rem' }}>
                  <div className="cta-icon-circle" style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaShieldAlt size={24} color="#d90000" />
                  </div>
                  <div className="cta-icon-circle" style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaServer size={24} color="#d90000" />
                  </div>
                  <div className="cta-icon-circle" style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaCloud size={24} color="#d90000" />
                  </div>
                </div>
                
                <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.5rem' }}>
                  {backupT.cta.title}
                </h3>
                <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', opacity: 0.9, maxWidth: '800px', margin: '0 auto 2.5rem auto' }}>
                  {backupT.cta.sub}
                </p>
                
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <Button variant="light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2rem', fontWeight: 600 }}
                    onClick={() => {
                        window.location.href = '/#contact';
                        setTimeout(() => {
                          document.getElementById('contact')?.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }, 100);
                      }}
                  >
                    <FaPhone className="me-2" />
                    {backupT.cta.btn1}
                  </Button>
                  <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2rem', fontWeight: 600, borderWidth: '2px' }}>
                    <FaExternalLinkAlt className="me-2" />
                    {backupT.cta.btn2}
                  </Button>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Backup;